import React from 'react';
import { isString } from 'lodash';
import Tooltip from '../../../elements/mui/Tooltip';
import ResizableGrid from '../../../elements/ResizableGrid';
import LoadableMonacoEditor from '../../../elements/editors/monaco/LoadableMonacoEditor';
import { formatCode } from 'js/lib/FormatUtils';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import { Box } from 'new/elements/Box';
import { CircularProgress, Typography } from '@mui/material';
import { ReactComponent as TableIcon } from 'new/components/icons/fi-rr-table.svg';
import { ReactComponent as RestApiIcon } from 'new/components/icons/icon-rest-api.svg';

const styles = {
  iconStyles: {
    fill: 'var(--color-greyscale-800)',
    width: '16px',
    cursor: 'pointer',
    marginRight: '4px',
  },
  headerIconStyle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: 'var(--color-greyscale-400)',
    color: 'var(--color-greyscale-800)',
    fontSize: '12px',
    lineHeight: '16px',
    padding: '4px 8px',
    borderRadius: '100px',
  },
  headerIconStyleLightMode: {
    backgroundColor: 'transparent',
    border: '1px solid var(--color-greyscale-400)',
  },
  headerIconStyleActive: {
    backgroundColor: 'var(--color-greyscale-0)',
  },
  headerIconStyleActiveLightMode: {
    backgroundColor: 'var(--color-greyscale-200)',
    border: '1px solid var(--color-greyscale-400)',
  },
};

class DatabaseSamples extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      viewMode: SchemaEditorViewModeType.TABLE,
    };
    this._getContent = this._getContent.bind(this);
    this._getCellContent = this._getCellContent.bind(this);
    this._getRowId = this._getRowId.bind(this);
    this._getAllowRowResize = this._getAllowRowResize.bind(this);
    this._getAllowColumnResize = this._getAllowColumnResize.bind(this);
    this._getDefaultRowHeight = this._getDefaultRowHeight.bind(this);
    this._getDefaultColumnWidth = this._getDefaultColumnWidth.bind(this);
    this._forceUpdateGrid = this._forceUpdateGrid.bind(this);
    this._handleViewModeSelected = this._handleViewModeSelected.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.viewMode === SchemaEditorViewModeType.TABLE &&
      this.props.lastUpdated !== prevProps.lastUpdated
    ) {
      this._forceUpdateGrid();
    }
  }

  render() {
    const { viewMode } = this.state;
    const content = this._getContent();
    const title = this.props.title || 'Data Samples';
    const isLightMode = this.props.stylingVariant === 'light';
    const baseStyle = isLightMode
      ? { ...styles.headerIconStyle, ...styles.headerIconStyleLightMode }
      : styles.headerIconStyle;
    const activeStyle = isLightMode
      ? styles.headerIconStyleActiveLightMode
      : styles.headerIconStyleActive;

    return (
      <Box
        title={
          <div className="database-samples-title-container">
            <Typography variant="subtitle2" className="database-samples-title">
              {title}
            </Typography>

            <div className="view-mode-buttons">
              <Tooltip title="View samples as columns in a table" arrow={true}>
                <div
                  className="schemaRowIconButton schema-editor-input schema-mode-row"
                  data-view-mode={SchemaEditorViewModeType.TABLE}
                  onClick={this._handleViewModeSelected}
                  style={{
                    ...baseStyle,
                    ...(viewMode === SchemaEditorViewModeType.TABLE ? activeStyle : {}),
                    ...(!content && {
                      backgroundColor: isLightMode
                        ? 'var(--color-greyscale-100)'
                        : 'var(--color-greyscale-400)',
                      pointerEvents: 'none',
                    }),
                  }}
                >
                  <TableIcon style={styles.iconStyles} />
                  Table
                </div>
              </Tooltip>
              <Tooltip title="View samples as nested JSON objects" arrow={true}>
                <div
                  className="codeIconButton schema-editor-input schema-mode-code"
                  data-view-mode={SchemaEditorViewModeType.CODE}
                  onClick={this._handleViewModeSelected}
                  style={{
                    ...baseStyle,
                    ...(viewMode === SchemaEditorViewModeType.CODE ? activeStyle : {}),
                    ...(!content && {
                      backgroundColor: isLightMode
                        ? 'var(--color-greyscale-100)'
                        : 'var(--color-greyscale-400)',
                      pointerEvents: 'none',
                    }),
                  }}
                >
                  <RestApiIcon style={styles.iconStyles} />
                  JSON
                </div>
              </Tooltip>
            </div>
          </div>
        }
        className={`database-samples-box${isLightMode ? ' database-samples-box--light-mode' : ''}`}
      >
        <div className="database-samples">
          <div className="database-samples-content">
            <div className="database-samples-content-inner-container">
              {this.props.isLoading ? (
                <div
                  className="database-samples-loading"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    minHeight: 120,
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography variant="bodySmall" className="file-preview-description">
                    Loading samples…
                  </Typography>
                </div>
              ) : content ? (
                content
              ) : (
                <Typography variant="bodySmall" className="file-preview-description">
                  Fetched data will appear here{' '}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </Box>
    );
  }

  _getContent() {
    const { samples } = this.props;
    const { viewMode } = this.state;

    // Check for error conditions: errorCode !== 200 or statusCode !== 200
    const isError =
      samples &&
      ((samples.errorCode && samples.errorCode !== 200) ||
        (samples.statusCode && samples.statusCode !== 200));

    if (isError) {
      const errorMessage =
        samples.error ||
        samples.errorMessage ||
        samples.response ||
        'There was an error fetching the data samples';
      return (
        <div className="info-text error-content" style={{ color: 'var(--color-secondary-700)' }}>
          {errorMessage}
        </div>
      );
    }

    if (samples && samples.response && samples.response.data && samples.response.columns) {
      const editorContent = formatCode(JSON.stringify(samples.response.data), 'application/json');

      return viewMode === SchemaEditorViewModeType.CODE ? (
        <LoadableMonacoEditor
          value={editorContent}
          language="json"
          theme="dark"
          height="100%"
          options={{
            readOnly: true,
            minimap: { enabled: false },
          }}
        />
      ) : (
        <ResizableGrid
          onRef={(g) => {
            if (g) {
              this._grid = g;
            }
          }}
          className="FreeGrid"
          rowCount={samples.response.data.length + 1}
          columnCount={samples.response.columns.length + 1}
          fixedRowCount={1}
          getDefaultRowHeight={this._getDefaultRowHeight}
          getDefaultColumnWidth={this._getDefaultColumnWidth}
          overscanRowCount={5}
          overscanColumnCount={2}
          getCellContent={this._getCellContent}
          getAllowRowResize={this._getAllowRowResize}
          getAllowColumnResize={this._getAllowColumnResize}
          getRowId={this._getRowId}
        />
      );
    }

    return null;
  }

  _getCellContent({ rowIndex, columnIndex }) {
    const {
      samples: {
        response: { data, columns },
      },
    } = this.props;

    if (rowIndex === 0 && columnIndex === 0) {
      return <div className="database-heading-cell" />;
    }

    const effectiveColumnIndex = columnIndex - 1;
    const columnName = columns[effectiveColumnIndex];

    if (rowIndex === 0) {
      return <div className="database-heading-cell">{columnName}</div>;
    }

    if (columnIndex === 0) {
      return <div className="database-row-number-cell">{rowIndex}</div>;
    }

    const effectiveRowIndex = rowIndex - 1;
    const cellData = data[effectiveRowIndex][columnName];

    return (
      <div className="database-sample-cell">
        {isString(cellData) ? cellData : JSON.stringify(cellData)}
      </div>
    );
  }

  _getRowId(index) {
    return index;
  }

  _getAllowRowResize() {
    return false;
  }

  _getAllowColumnResize({ rowIndex, columnIndex }) {
    return rowIndex === 0 && columnIndex > 0;
  }

  _getDefaultRowHeight() {
    return 36;
  }

  _getDefaultColumnWidth(index, ...args) {
    if (index === 0) {
      return 60;
    }

    return 160;
  }

  _forceUpdateGrid() {
    if (this._grid) {
      this._grid.forceUpdateGrids();
    }
  }

  _handleViewModeSelected(event) {
    event.stopPropagation();

    const { viewMode } = event.currentTarget.dataset;
    this.setState({ viewMode });
  }
}

export default DatabaseSamples;
